import type {
  DocumentListOptions,
  DocumentPublishState,
  DocumentSortField,
  DocumentSourceType,
  DocumentStatus,
} from '../../types/knowledge';
import type { DocumentPageSize } from '../../types/userConfig';

export type DocumentSortState = {
  column: DocumentSortField;
  direction: 'ASC' | 'DESC';
};

export type DocumentFilterState = {
  debouncedFilename: string;
  source: DocumentSourceType | null;
  status: DocumentStatus | null;
};

/** Default: newest uploads first. */
export const DEFAULT_DOCUMENT_SORT: DocumentSortState = {
  column: 'created',
  direction: 'DESC',
};

export const DEFAULT_DOCUMENT_FILTER: DocumentFilterState = {
  debouncedFilename: '',
  source: null,
  status: null,
};

export const DOCUMENT_SEARCH_DEBOUNCE_MS = 500;
export const DOCUMENT_SEARCH_MIN_LENGTH = 3;

export function getNextDocumentSort(
  current: DocumentSortState,
  column: DocumentSortField,
): DocumentSortState {
  if (current.column !== column) {
    return { column, direction: 'ASC' };
  }

  if (current.direction === 'ASC') {
    return { column, direction: 'DESC' };
  }

  if (column === DEFAULT_DOCUMENT_SORT.column) {
    return { column, direction: 'ASC' };
  }

  return DEFAULT_DOCUMENT_SORT;
}

export function buildDocumentListOptions(
  sort: DocumentSortState,
  filter: DocumentFilterState,
  page: number,
  pageSize: DocumentPageSize,
  publishState?: DocumentPublishState,
): DocumentListOptions {
  const options: DocumentListOptions = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    sort: sort.column,
    direction: sort.direction,
  };

  if (filter.debouncedFilename.length >= DOCUMENT_SEARCH_MIN_LENGTH) {
    options.filename = filter.debouncedFilename;
  }

  if (filter.source) {
    options.source = filter.source;
  }

  if (filter.status) {
    options.status = filter.status;
  }

  if (publishState) {
    options.publishState = publishState;
  }

  return options;
}

export function parseDocumentSortFromSearchParams(
  searchParams: URLSearchParams,
): DocumentSortState {
  const sort = searchParams.get('sort');
  const direction = searchParams.get('direction');

  if (
    (sort === 'filename' ||
      sort === 'filesize' ||
      sort === 'created' ||
      sort === 'modified') &&
    (direction === 'ASC' || direction === 'DESC')
  ) {
    return { column: sort, direction };
  }

  return DEFAULT_DOCUMENT_SORT;
}

export function parseDocumentFilterFromSearchParams(
  searchParams: URLSearchParams,
): DocumentFilterState {
  const search = searchParams.get('search')?.trim() ?? '';
  const source = searchParams.get('source');
  const status = searchParams.get('status');

  return {
    debouncedFilename:
      search.length >= DOCUMENT_SEARCH_MIN_LENGTH ? search : '',
    source:
      source === 'UPLOAD' || source === 'CRAWL' || source === 'ARCHIVE_EXTRACT'
        ? source
        : null,
    status:
      status === 'PENDING' ||
      status === 'SCANNING' ||
      status === 'UPLOADING' ||
      status === 'EXTRACTING' ||
      status === 'EMBEDDING' ||
      status === 'READY' ||
      status === 'FAILED'
        ? status
        : null,
  };
}

export function parseDocumentPageFromSearchParams(
  searchParams: URLSearchParams,
): number {
  const pageValue = Number(searchParams.get('page') ?? '1');
  if (!Number.isFinite(pageValue) || pageValue < 1) {
    return 1;
  }
  return Math.floor(pageValue);
}

export function buildDocumentSearchParams({
  sort,
  filter,
  page,
  searchInput,
}: {
  sort: DocumentSortState;
  filter: DocumentFilterState;
  page: number;
  searchInput: string;
}): URLSearchParams {
  const params = new URLSearchParams();

  const trimmedSearch = searchInput.trim();
  if (trimmedSearch !== '') {
    params.set('search', trimmedSearch);
  }

  const isDefaultSort =
    sort.column === DEFAULT_DOCUMENT_SORT.column &&
    sort.direction === DEFAULT_DOCUMENT_SORT.direction;
  if (!isDefaultSort) {
    params.set('sort', sort.column);
    params.set('direction', sort.direction);
  }

  if (filter.source) {
    params.set('source', filter.source);
  }

  if (filter.status) {
    params.set('status', filter.status);
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  return params;
}
